import { httpsCallable } from "firebase/functions";
import { functions } from "../config";

export interface SongResult {
  mbid: string;
  title: string;
  artist: string;
  year: number | null;
  coverArt: string | null;
}

export type SongStatus = "pending" | "played" | "rejected";

export interface SongRequestItem {
  id: string;
  title: string;
  artist: string;
  year: number | null;
  coverArt: string | null;
  likeCount: number;
  status: SongStatus;
  likedByMe: boolean;
  playedAt: number | null;
}

export type RequestSongOutcome =
  | "created"
  | "voted"
  | "resurrected"
  | "already"
  | "rejectedBlocked"
  | "needsConfirm";

const searchSongsCallable = httpsCallable<
  { query: string },
  { results: SongResult[] }
>(functions, "searchSongs");

export async function searchSongs(query: string): Promise<SongResult[]> {
  const { data } = await searchSongsCallable({ query });
  return data.results;
}

interface RequestSongArgs {
  siteId: string;
  deviceId: string;
  song: SongResult;
  confirmReadd?: boolean;
}

const requestSongCallable = httpsCallable<
  RequestSongArgs,
  { requestId: string; status: RequestSongOutcome }
>(functions, "requestSong");

export async function requestSong(
  args: RequestSongArgs,
): Promise<{ requestId: string; status: RequestSongOutcome }> {
  const { data } = await requestSongCallable(args);
  return data;
}

interface ToggleSongLikeArgs {
  siteId: string;
  deviceId: string;
  requestId: string;
  like: boolean;
}

const toggleSongLikeCallable = httpsCallable<
  ToggleSongLikeArgs,
  { requestId: string; liked: boolean }
>(functions, "toggleSongLike");

export async function toggleSongLike(args: ToggleSongLikeArgs): Promise<void> {
  await toggleSongLikeCallable(args);
}

interface ListSongRequestsArgs {
  siteId: string;
  deviceId?: string;
  djKey?: string;
  status?: SongStatus;
  sort?: "top" | "newest";
}

const listSongRequestsCallable = httpsCallable<
  ListSongRequestsArgs,
  { requests: SongRequestItem[]; isDj: boolean }
>(functions, "listSongRequests");

export async function listSongRequests(
  args: ListSongRequestsArgs,
): Promise<{ requests: SongRequestItem[]; isDj: boolean }> {
  const { data } = await listSongRequestsCallable(args);
  return data;
}

const verifyDjAccessCallable = httpsCallable<
  { siteId: string; code: string },
  { ok: boolean; token: string }
>(functions, "verifyDjAccess");

export async function verifyDjAccess(
  siteId: string,
  code: string,
): Promise<string> {
  const { data } = await verifyDjAccessCallable({ siteId, code });
  return data.token;
}

interface SetSongRequestStatusArgs {
  siteId: string;
  requestId: string;
  status: SongStatus;
  djKey: string;
}

const setSongRequestStatusCallable = httpsCallable<
  SetSongRequestStatusArgs,
  { requestId: string; status: SongStatus }
>(functions, "setSongRequestStatus");

export async function setSongRequestStatus(
  args: SetSongRequestStatusArgs,
): Promise<void> {
  await setSongRequestStatusCallable(args);
}
